import type { DataTableColumn } from './types';

function escapeCell(value: string): string {
  // RFC 4180: quote if the value contains a comma, quote, CR, or LF; double
  // any embedded quotes.
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function defaultRowValue<T>(row: T, col: DataTableColumn<T>): string {
  if (col.exportValue) {
    const v = col.exportValue(row);
    return v === null || v === undefined ? '' : String(v);
  }
  // Fall back to the rendered cell. ReactNode → string is best-effort: only
  // primitives serialise cleanly; for richer cells the caller should set
  // exportValue.
  const node = col.cell(row);
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  return '';
}

export function rowsToCsv<T>(rows: T[], columns: DataTableColumn<T>[]): string {
  const cols = columns.filter((c) => !c.exportSkip);
  const header = cols.map((c) => escapeCell(c.header)).join(',');
  const body = rows
    .map((row) => cols.map((col) => escapeCell(defaultRowValue(row, col))).join(','))
    .join('\n');
  return body ? `${header}\n${body}\n` : `${header}\n`;
}

export function downloadCsv(filename: string, csv: string): void {
  if (typeof window === 'undefined') return;
  // BOM keeps Excel happy for UTF-8.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
